import { useCallback, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { getStoredToken } from "./authStorage";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

const outlineButtonSx = {
  borderColor: "#ff6600",
  color: "#ff6600",
  fontWeight: 700,
  letterSpacing: 0,
  textTransform: "uppercase",
  "&:hover": {
    backgroundColor: "#ff6600",
    color: "#000",
    borderColor: "#ff6600",
  },
};

const primaryButtonSx = {
  backgroundColor: "#2374d5",
  color: "#fff",
  fontWeight: 700,
  letterSpacing: 0,
  textTransform: "uppercase",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1b5fb2",
    boxShadow: "none",
  },
  "&:disabled": {
    backgroundColor: "#2b333b",
    color: "#77818b",
  },
};

const dangerButtonSx = {
  backgroundColor: "#b42318",
  color: "#fff",
  fontWeight: 700,
  letterSpacing: 0,
  textTransform: "uppercase",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#912018",
    boxShadow: "none",
  },
  "&:disabled": {
    backgroundColor: "#2b333b",
    color: "#77818b",
  },
};

const tableHeaderCellSx = {
  backgroundColor: "#252b31",
  borderBottom: "1px solid #46515d",
  color: "#f7f9fb",
  fontSize: "0.82rem",
  fontWeight: 800,
  letterSpacing: 0,
  py: 2,
};

const tableCellSx = {
  borderBottom: "1px solid #303842",
  color: "#eef2f5",
  fontSize: "0.95rem",
  py: 2,
};

const mutedCellSx = {
  ...tableCellSx,
  color: "#aab4bf",
};

const dialogPaperSx = {
  backgroundColor: "#15191d",
  border: "1px solid #38424d",
  color: "#f5f7fa",
};

const dialogFieldSx = {
  "& .MuiInputLabel-root": {
    color: "#aab4bf",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#ff6600",
  },
  "& .MuiOutlinedInput-root": {
    color: "#f5f7fa",
    "& fieldset": {
      borderColor: "#46525f",
    },
    "&:hover fieldset": {
      borderColor: "#718096",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#ff6600",
    },
  },
};

const readJson = async (response) => {
  try {
    return await response.json();
  } catch (err) {
    return {};
  }
};

const isProtectedUser = (user) => {
  return Boolean(user?.isProtected) || String(user?.username || "").toLowerCase() === "admin";
};

const UserManagement = () => {
  const { handleAuthError } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createUsername, setCreateUsername] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [verifyCreatePassword, setVerifyCreatePassword] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [verifyPassword, setVerifyPassword] = useState("");
  const [selectedDeleteUser, setSelectedDeleteUser] = useState(null);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const res = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/admin/users`, {
        headers: {
          Authorization: `Bearer ${getStoredToken()}`,
        },
      });
      const data = await readJson(res);

      if (res.status === 401) {
        handleAuthError(data.message || "Your session expired. Please log in again.");
        return;
      }

      if (!res.ok || !data.status) {
        throw new Error(
          data.message || "User management is not available. Deploy the updated auth server first."
        );
      }

      setUsers(data.users || []);
    } catch (err) {
      console.error("Could not load users:", err);
      setMessage({ severity: "error", text: err.message || "Could not load users" });
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const openCreateDialog = () => {
    setCreateUsername("");
    setCreatePassword("");
    setVerifyCreatePassword("");
    setMessage(null);
    setIsCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setCreateUsername("");
    setCreatePassword("");
    setVerifyCreatePassword("");
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setVerifyPassword("");
    setMessage(null);
  };

  const closeResetDialog = () => {
    setSelectedUser(null);
    setNewPassword("");
    setVerifyPassword("");
  };

  const openDeleteDialog = (user) => {
    if (isProtectedUser(user)) {
      setMessage({ severity: "error", text: "The admin account cannot be deleted" });
      return;
    }

    setSelectedDeleteUser(user);
    setMessage(null);
  };

  const closeDeleteDialog = () => {
    setSelectedDeleteUser(null);
  };

  const createUser = async () => {
    if (createPassword !== verifyCreatePassword) {
      setMessage({ severity: "error", text: "Passwords do not match" });
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/admin/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getStoredToken()}`,
        },
        body: JSON.stringify({
          username: createUsername,
          password: createPassword,
        }),
      });
      const data = await readJson(res);

      if (res.status === 401) {
        handleAuthError(data.message || "Your session expired. Please log in again.");
        return;
      }

      if (!res.ok || !data.status) {
        throw new Error(
          data.message || "User creation is not available. Deploy the updated auth server first."
        );
      }

      closeCreateDialog();
      setMessage({ severity: "success", text: data.message || "User created" });
      loadUsers();
    } catch (err) {
      console.error("Could not create user:", err);
      setMessage({ severity: "error", text: err.message || "Could not create user" });
    }
  };

  const resetPassword = async () => {
    if (!selectedUser) return;
    if (newPassword !== verifyPassword) {
      setMessage({ severity: "error", text: "Passwords do not match" });
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_AUTH_API_URL}/admin/users/${selectedUser.id}/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getStoredToken()}`,
          },
          body: JSON.stringify({ newPassword }),
        }
      );
      const data = await readJson(res);

      if (res.status === 401) {
        handleAuthError(data.message || "Your session expired. Please log in again.");
        return;
      }

      if (!res.ok || !data.status) {
        throw new Error(
          data.message || "Password reset is not available. Deploy the updated auth server first."
        );
      }

      closeResetDialog();
      setMessage({ severity: "success", text: data.message || "Password reset" });
    } catch (err) {
      console.error("Could not reset password:", err);
      setMessage({ severity: "error", text: err.message || "Could not reset password" });
    }
  };

  const deleteSelectedUser = async () => {
    if (!selectedDeleteUser) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_AUTH_API_URL}/admin/users/${selectedDeleteUser.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${getStoredToken()}`,
          },
        }
      );
      const data = await readJson(res);

      if (res.status === 401) {
        handleAuthError(data.message || "Your session expired. Please log in again.");
        return;
      }

      if (!res.ok || !data.status) {
        throw new Error(
          data.message || "User deletion is not available. Deploy the updated auth server first."
        );
      }

      closeDeleteDialog();
      setMessage({ severity: "success", text: data.message || "User deleted" });
      loadUsers();
    } catch (err) {
      console.error("Could not delete user:", err);
      setMessage({ severity: "error", text: err.message || "Could not delete user" });
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#050505",
        color: "#fff",
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: "#ff6600", fontWeight: 800, letterSpacing: 0 }}>
            Bullet Library Users
          </Typography>
          <Typography sx={{ color: "#c8d0d8", mt: 0.5 }}>
            These accounts can sign in to manage bullet records.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Button variant="outlined" href="/dashboard" sx={outlineButtonSx}>
            Dashboard
          </Button>
          <Button variant="outlined" href="/" sx={outlineButtonSx}>
            Bullet Library
          </Button>
          <Button variant="contained" onClick={openCreateDialog} sx={primaryButtonSx}>
            Add User
          </Button>
          <Button variant="contained" onClick={loadUsers} disabled={isLoading} sx={primaryButtonSx}>
            Refresh
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 2, borderRadius: 1 }}>
          {message.text}
        </Alert>
      )}

      <Paper
        sx={{
          backgroundColor: "#15191d",
          border: "1px solid #38424d",
          borderRadius: 1,
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            borderBottom: "1px solid #303842",
            display: "flex",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
          }}
        >
          <Typography sx={{ color: "#f5f7fa", fontWeight: 700 }}>Users</Typography>
          <Typography sx={{ color: "#aab4bf", fontSize: "0.9rem" }}>
            {isLoading ? "Loading" : `${users.length} ${users.length === 1 ? "user" : "users"}`}
          </Typography>
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeaderCellSx, width: 96 }}>ID</TableCell>
                <TableCell sx={tableHeaderCellSx}>Username</TableCell>
                <TableCell align="right" sx={tableHeaderCellSx}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow
                  key={user.id}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#1d242b",
                    },
                  }}
                >
                  <TableCell sx={mutedCellSx}>{user.id}</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 700 }}>
                    <Box sx={{ alignItems: "center", display: "flex", gap: 1.25 }}>
                      <span>{user.username}</span>
                      {isProtectedUser(user) && (
                        <Typography
                          component="span"
                          sx={{
                            backgroundColor: "#2b333b",
                            border: "1px solid #46515d",
                            borderRadius: 1,
                            color: "#c8d0d8",
                            fontSize: "0.72rem",
                            fontWeight: 800,
                            lineHeight: 1,
                            px: 1,
                            py: 0.55,
                            textTransform: "uppercase",
                          }}
                        >
                          Protected
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={tableCellSx}>
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => openResetDialog(user)}
                        sx={primaryButtonSx}
                      >
                        Reset Password
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => openDeleteDialog(user)}
                        disabled={isProtectedUser(user)}
                        sx={dangerButtonSx}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} sx={tableCellSx}>
                    No users found.
                  </TableCell>
                </TableRow>
              )}
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={3} sx={tableCellSx}>
                    Loading users...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isCreateDialogOpen} onClose={closeCreateDialog} PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ color: "#ff6600", fontWeight: 800 }}>Add User</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 1,
            minWidth: { xs: 280, sm: 360 },
          }}
        >
          <TextField
            fullWidth
            label="Username"
            value={createUsername}
            onChange={(e) => setCreateUsername(e.target.value)}
            sx={dialogFieldSx}
          />
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={createPassword}
            onChange={(e) => setCreatePassword(e.target.value)}
            sx={dialogFieldSx}
          />
          <TextField
            fullWidth
            type="password"
            label="Retype Password"
            value={verifyCreatePassword}
            onChange={(e) => setVerifyCreatePassword(e.target.value)}
            sx={dialogFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeCreateDialog} sx={outlineButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createUser} sx={primaryButtonSx}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!selectedUser} onClose={closeResetDialog} PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ color: "#ff6600", fontWeight: 800 }}>Reset Password</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 1,
            minWidth: { xs: 280, sm: 360 },
          }}
        >
          <Typography sx={{ color: "#d9e0e6" }}>
            Reset password for {selectedUser?.username}
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={dialogFieldSx}
          />
          <TextField
            fullWidth
            type="password"
            label="Retype New Password"
            value={verifyPassword}
            onChange={(e) => setVerifyPassword(e.target.value)}
            sx={dialogFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeResetDialog} sx={outlineButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={resetPassword} sx={primaryButtonSx}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selectedDeleteUser}
        onClose={closeDeleteDialog}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ color: "#ff6600", fontWeight: 800 }}>Delete User</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 1,
            minWidth: { xs: 280, sm: 360 },
          }}
        >
          <Typography sx={{ color: "#d9e0e6" }}>
            Delete {selectedDeleteUser?.username}? This account will no longer be able to sign in.
          </Typography>
          <Typography sx={{ color: "#aab4bf", fontSize: "0.9rem" }}>
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeDeleteDialog} sx={outlineButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={deleteSelectedUser} sx={dangerButtonSx}>
            Delete User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
